import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://maldaturf.in';
const DEFAULT_OG = `${SITE_URL}/og-image.jpg`;

/**
 * Per-page SEO component using react-helmet-async.
 * Sets <title>, <meta description>, canonical URL, OG, and Twitter tags.
 *
 * Props:
 *  - title: page title (will be suffixed with "| Malda Turf" automatically)
 *  - description: meta description (~160 chars)
 *  - path: canonical path (e.g. "/booking")
 *  - image: OG image URL (defaults to site default)
 *  - noindex: set true to add <meta name="robots" content="noindex"> (private routes)
 *  - jsonLd: optional object to inject as application/ld+json
 */
export function SEO({ title, description, path = '/', image = DEFAULT_OG, noindex = false, jsonLd }) {
  const fullTitle = title ? `${title} | Malda Turf` : 'Malda Turf — Premium Cricket & Football Turf | Book Online in Malda';
  const desc = description || 'Book hourly cricket and football turf slots online at Malda Turf. Premium grass, floodlights, instant confirmation, secure online payments. Open 24×7 in Malda, West Bengal.';
  const url = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Malda Turf" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:url" content={url} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export default SEO;
